import { Outlet, useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

export default function MainLayout() {
  const { lang } = useParams<{ lang: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // URLの言語に合わせてi18nを更新
  useEffect(() => {
    if (lang && ['ja', 'en'].includes(lang) && lang !== i18n.language) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // 言語切り替え
  const toggleLanguage = () => {
    const newLang = i18n.language === 'ja' ? 'en' : 'ja';
    const stripLangFromPath = (pathname: string) => pathname.replace(/^\/(ja|en)(?=\/|$)/, '');
    const currentPath = stripLangFromPath(location.pathname);
    navigate(`/${newLang}${currentPath}${location.search}${location.hash}`);
  };

  // Topページかどうか判定（ホームリンク非表示用）
  const isTopPage = location.pathname === `/${lang}` || location.pathname === `/${lang}/`;

  return (
    <div className="min-h-screen flex flex-col">
      {/* ヘッダー */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-cosmos-950/80 backdrop-blur-md border-b border-cosmos-800/50">
        <nav className="container-wide">
          <div className="flex items-center justify-between h-24">
            {/* ロゴ（アイコンのみ） */}
            <Link to={`/${lang}`} className="flex items-center">
              <img src="/etc/icon.png" alt="GREAT HERO'S JOURNEY" className="h-20 w-20" />
            </Link>

            {/* ナビゲーション */}
            <div className="hidden md:flex items-center gap-10">
              {/* Topページではホームリンクを非表示 */}
              {!isTopPage && (
                <Link to={`/${lang}`} className="text-xl text-gray-300 hover:text-golden-400 transition">
                  {t('common.nav.home')}
                </Link>
              )}
              <Link to={`/${lang}/story`} className="text-xl text-gray-300 hover:text-golden-400 transition">
                {t('common.nav.story')}
              </Link>
              <Link to={`/${lang}/contents`} className="text-xl text-gray-300 hover:text-golden-400 transition">
                {t('common.nav.contents')}
              </Link>
              <Link to={`/${lang}/events`} className="text-xl text-gray-300 hover:text-golden-400 transition">
                {t('common.nav.events')}
              </Link>
              <Link to={`/${lang}/contact`} className="text-xl text-gray-300 hover:text-golden-400 transition">
                {t('common.nav.contact')}
              </Link>
            </div>

            {/* 言語切り替え */}
            <button
              onClick={toggleLanguage}
              className="px-4 py-2 text-lg border border-cosmos-600 rounded-full text-gray-300 hover:border-golden-500 hover:text-golden-400 transition"
            >
              {i18n.language === 'ja' ? 'EN' : '日本語'}
            </button>
          </div>
        </nav>
      </header>

      {/* メインコンテンツ */}
      <main className="flex-1 pt-24">
        <Outlet />
      </main>

      {/* フッター */}
      <footer className="bg-cosmos-950 border-t border-cosmos-800/50 py-16">
        <div className="container-wide text-center">
          {/* v0.7.0: HeroSection のキャッチと同じスタイルに統一 */}
          <p className="text-white/90 font-serif text-2xl tracking-[0.22em] drop-shadow-[0_6px_24px_rgba(0,0,0,0.85)] mb-4">{t('common.footer.message')}</p>
          <p className="text-gray-400 text-lg">{t('common.footer.copyright')}</p>
        </div>
      </footer>
    </div>
  );
}
